/**
 * Main layout component for the home page, including posts and side panels.
 * 首頁主要佈局組件，包含貼文與側欄。
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import LeftSidebar from '@/components/layout/LeftSidebar';
import RightSidebar from '@/components/layout/RightSidebar';
import Hashtag from './Hashtag';
import MyPost from '@/features/posts/components/MyPost';
import PostCard from '@/features/posts/components/PostCard';
import Axios from '@/shared/api/axios';
import { useTranslation } from 'react-i18next';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { getContentItemId } from '@/shared/utils/contentItemId';
import StatusModal from '@/components/ui/StatusModal';

function HomeLayout({ idolId }) {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    // 目前生效中的標籤篩選（逗號分隔的 tag id 字串），null 代表一般模式
    const [activeTags, setActiveTags] = useState(() => {
        const tagsParam = searchParams.get('tags');
        const tagParam = searchParams.get('tag');
        return tagsParam || tagParam || null;
    });

    // 共享的標籤數據狀態
    const [tagsData, setTagsData] = useState(null);
    const [tagsRefreshKey, setTagsRefreshKey] = useState(0);

    // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
    const statusModalRef = useRef(null);
    const [currentStatusCode, setCurrentStatusCode] = useState('');

    // 獲取標籤數據
    const getTagsData = async () => {
        try {
            const res = await Axios.get("/post/get-hot-tags");
            switch (res.data.status_code) {
                case 'SYSTEM_1000':
                    setTagsData(res.data.data);
                    setTagsRefreshKey(prev => prev + 1);
                    break;
                default:
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    break;
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Error fetching tags: ', error);
            }
        }
    };

    // 生效中的 tag id 陣列（撈列表、判斷新貼文是否符合當前篩選共用）
    const activeTagIds = useMemo(
        () => (activeTags ? activeTags.split(',').filter(tag => tag.trim()).map(tag => parseInt(tag.trim())) : []),
        [activeTags]
    );

    // 取得貼文列表（單一頁）：有標籤篩選時走標籤模式（混合貼文+世界觀），否則走一般模式（純貼文）
    // 分頁狀態、page 1 覆蓋/page>1 去重 append、無限滾動皆交由 usePaginatedList 統一處理
    const fetchPage = async (pageNum) => {
        try {
            if (activeTags) {
                const res = await Axios.post("/post/get-tag-post-and-world-list", {
                    idol_id: idolId,
                    page: pageNum,
                    limit: 10,
                    tags: activeTagIds
                });
                if (res.data.status_code !== 'SYSTEM_1000') {
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    return null;
                }
                return { data: res.data.data, total: res.data.total };
            }

            const res = await Axios.post("/post/get-list", {
                idol_id: idolId,
                page: pageNum,
                limit: 10
            });
            if (res.data.status_code !== 'SYSTEM_1000') {
                setCurrentStatusCode(res.data.status_code);
                statusModalRef.current?.show();
                return null;
            }
            return { data: res.data.data, total: res.data.total };
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Error fetching post list: ', error);
            }
            return null;
        }
    };

    const { data: postInfo, setData: setPostInfo, isLoading, reset } = usePaginatedList({
        fetchPage,
        getId: getContentItemId,
        limit: 10,
    });

    // 點擊 hashtag 時寫入 ?tags，與 PostFilter 打勾共用下方的 URL 同步，PostFilter 才會跟著勾選對應標籤
    const updateListWithTag = (tagId) => {
        setSearchParams({ tags: tagId });
    };

    // 貼文標籤異動後刷新熱門標籤數據（usage_count 可能變動）
    const refreshPostFilterTags = () => {
        getTagsData();
    };

    // 本地更新/移除單一貼文或世界觀，取代整包 refetch（避免按讚/收藏/留言等高頻互動時列表跳動）
    // updater 回傳 null/undefined 代表移除該筆
    const patchItem = (itemId, updater) => {
        setPostInfo(prev => prev.reduce((acc, item) => {
            const currentId = getContentItemId(item);
            if (currentId !== itemId) {
                acc.push(item);
                return acc;
            }
            const next = updater(item);
            if (next) acc.push(next);
            return acc;
        }, []));
    };

    // 發文（含圖片上傳）可能耗時數十秒，期間使用者可能切換標籤，
    // 而 MyPost 的回呼捕獲的是按下送出當下的閉包，用 ref 讓判斷讀到當下生效的篩選
    const activeTagIdsRef = useRef(activeTagIds);
    activeTagIdsRef.current = activeTagIds;

    // 發文成功後：用後端回傳的該筆直接插到列表最前面，取代整包 reset
    // （reset 會回第一頁，無限滾動已載入的頁數被砍光、捲動位置跳掉，與其他互動改本地更新的理由相同）
    // 標籤模式的列表只含符合篩選的內容，新貼文沒帶到生效中的 tag 就不該出現在這裡
    const prependPost = (newPost) => {
        const tagIds = activeTagIdsRef.current;
        if (tagIds.length > 0 && !newPost.post_info.post_tags.some(tag => tagIds.includes(tag.tag_id))) return;
        setPostInfo(prev => [newPost, ...prev]);
    };

    // 初始載入標籤數據
    useEffect(() => {
        getTagsData();
    }, []);

    // 同步 URL ?tags/?tag 參數（PostFilter 打勾、或從其他頁面導入帶標籤）到本地篩選狀態
    useEffect(() => {
        const tagsParam = searchParams.get('tags');
        const tagParam = searchParams.get('tag');
        setActiveTags(tagsParam || tagParam || null);
    }, [searchParams]);

    // 標籤篩選條件改變時（含初始載入），重新從第一頁載入
    useEffect(() => {
        reset();
    }, [activeTags]);

    return (
        <PageLayout
            leftSidebar={
                <LeftSidebar
                    showHashtag={true}
                    tagsData={tagsData}
                    tagsRefreshKey={tagsRefreshKey}
                    showFooter={true}
                    currentPage="home"
                />
            }
            rightSidebar={<RightSidebar />}
            isLoading={isLoading}
        >
            {/* 手機版標籤篩選器 */}
            <div className="d-lg-none mb-3">
                <Hashtag
                    variant="mobile"
                    tagsData={tagsData}
                    refreshKey={tagsRefreshKey}
                />
            </div>

            {/* 發文區 */}
            <MyPost onPostCreated={prependPost} onTagsChanged={refreshPostFilterTags} />

            {/* 貼文列表 */}
            {
                postInfo.length > 0 ? (
                    postInfo.map((item) => {
                        // 判斷內容類型
                        const isWorldContent = item.class === 'world' || item.world_info;
                        const isPostContent = item.class === 'post' || item.post_info;
                        const itemId = getContentItemId(item);

                        if (isWorldContent) {
                            return (
                                <PostCard
                                    key={itemId}
                                    data={item}
                                    contentType="world"
                                    onItemChange={(updater) => patchItem(itemId, updater)}
                                    updateList={reset}
                                    onTagsChanged={refreshPostFilterTags}
                                    onTagClick={updateListWithTag}
                                    showBadge={!!activeTags}
                                />
                            );
                        } else if (isPostContent) {
                            return (
                                <PostCard
                                    key={itemId}
                                    data={item}
                                    contentType="post"
                                    onItemChange={(updater) => patchItem(itemId, updater)}
                                    updateList={reset}
                                    onTagsChanged={refreshPostFilterTags}
                                    onTagClick={updateListWithTag}
                                    showBadge={!!activeTags}
                                />
                            );
                        } else {
                            return null;
                        }
                    })
                ) : (
                    <div className="text-center">{t("no_post_data")}</div>
                )
            }
            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
        </PageLayout>
    );
}

export default HomeLayout;
